import React, { useCallback } from 'react';
import { CheckboxProps } from 'components/Checkbox';
import styled from 'styled-components';
import {
  Space,
  Button,
  Icon,
  Dropdown,
  Menu,
  MenuItem,
  Checkbox,
  theme,
  Tooltip,
  Modal,
} from 'components';
import { useHistory } from 'react-router-dom';
import { featureEnabled } from 'utils';
import EllipsisMenu from './EllipsisMenu';
import SearchBar from './SearchBar';
import ShowingsDropdown from './ShowingsDropdown';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  AgentCalendarMenu,
  StatusMenuItem,
  ShowingsCalendarAppointmentStatus,
  isAppointmentStatus,
} from '../types';
import { getStatusMenuItems } from '../constants';
import CalendarSyncButton from './CalendarSyncButton';
import DateRangePicker from '../DateRangePicker';
import AgentUserCalendarContext from '../AgentUserCalendarContext';

type ToolBarDesktopProps = {
  localStatuses: ShowingsCalendarAppointmentStatus[] | undefined;
  setLocalStatuses: React.Dispatch<
    React.SetStateAction<ShowingsCalendarAppointmentStatus[] | undefined>
  >;
  statusOnChange: (
    key: ShowingsCalendarAppointmentStatus,
  ) => CheckboxProps['onChange'];
  dateRangeOnChange: (date: [moment.Moment, moment.Moment]) => void;
};

const ToolBarDesktop: React.FC<ToolBarDesktopProps> = ({
  localStatuses,
  setLocalStatuses,
  statusOnChange,
  dateRangeOnChange,
}) => {
  const history = useHistory();
  const context = useShowingsCalendarContext();

  const {
    calendarType,
    setStatuses,
    date,
    selectedMenuOption,
    listingId,
    setPendingStatus,
    clearStatuses,
    isLimitedAccessOnNAB,
    appointmentConfig,
    listing,
  } = context;

  const agentUserCalendarContext = React.useContext(AgentUserCalendarContext);

  const [statusOverlayVisible, setStatusOverlayVisible] =
    React.useState<boolean>(false);

  const clearHandler = React.useCallback(() => {
    setLocalStatuses([]);
    clearStatuses();
    setStatusOverlayVisible(false);
  }, [setLocalStatuses, clearStatuses]);

  const saveHandler = () => {
    const filteredLocalStatuses = localStatuses?.filter(isAppointmentStatus);
    setPendingStatus(localStatuses.includes('pending'));
    setStatuses(filteredLocalStatuses);
    setStatusOverlayVisible(false);
  };

  const handleNavigateToBookingAppointment = useCallback(() => {
    if (
      listing?.org.features.includes('PRE_BOOKING') &&
      appointmentConfig?.preBookingInformation
    ) {
      Modal.confirm({
        icon: null,
        title: 'Important Information',
        content: `${appointmentConfig?.preBookingInformation}`,
        okText: 'Continue to booking',
        cancelText: 'Cancel',
        closable: true,
        onOk() {
          history.push(`/listing/${listingId}/appointments/book`);
        },
      });
    } else {
      history.push(`/listing/${listingId}/appointments/book`);
    }
  }, [
    appointmentConfig?.preBookingInformation,
    history,
    listing?.org.features,
    listingId,
  ]);

  return (
    <StyledHeaderContainer wrap>
      {calendarType === 'listing' ? (
        <>
          <ShowingsDropdown />

          <Tooltip
            title={
              isLimitedAccessOnNAB &&
              'You cannot Book a Showing as this listing is not represented by BrokerBay'
            }
            overlayInnerStyle={{ width: 340 }}
          >
            <div>
              {featureEnabled('preBookingNotificationFeature') ? (
                <Button
                  id="book-showing-button"
                  onClick={handleNavigateToBookingAppointment}
                  icon={
                    <Icon
                      type="home"
                      color={isLimitedAccessOnNAB ? 'grey' : 'white'}
                      height={15}
                      width={15}
                    />
                  }
                  data-cy="book-showing-button"
                  disabled={isLimitedAccessOnNAB}
                  type="primary"
                >
                  Book Showing
                </Button>
              ) : (
                <Button
                  id="book-showing-button"
                  href={`#/listing/${listingId}/appointments/book`}
                  icon={
                    <Icon
                      type="home"
                      color={isLimitedAccessOnNAB ? 'grey' : 'white'}
                      height={15}
                      width={15}
                    />
                  }
                  data-cy="book-showing-button"
                  disabled={isLimitedAccessOnNAB}
                  type="primary"
                >
                  Book Showing
                </Button>
              )}
            </div>
          </Tooltip>

          <Button
            id="show-configuration-button"
            href={`#/listing/${listingId}/appointments/config`}
            icon={<Icon type="settings" color="black" width={15} height={15} />}
            data-cy="showing-config-button"
          >
            Showing Configuration
          </Button>
          <Button
            id="feedback-settings-button"
            href={`#/listing/${listingId}/appointments/feedback_settings`}
            icon={
              <AlignedIcon
                type="antdLikeFilled"
                color="black"
                width={15}
                height={15}
              />
            }
            data-cy="feedback-settings-button"
          >
            Feedback Settings
          </Button>
          {featureEnabled('chronologicalShowings') && (
            <>
              <DateRangePicker value={date} onChange={dateRangeOnChange} />
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                open={statusOverlayVisible}
                onOpenChange={visible => setStatusOverlayVisible(visible)}
                dropdownRender={() => (
                  <StyledMenu>
                    {getStatusMenuItems({
                      isBuyer: false,
                    }).map(({ label, key }: StatusMenuItem) => (
                      <StyledMenuItem key={key}>
                        <Checkbox
                          onChange={statusOnChange(key)}
                          checked={localStatuses?.includes(key)}
                          id={`${key}-checkbox`}
                          data-cy={`${key}-checkbox`}
                        >
                          {label}
                        </Checkbox>
                      </StyledMenuItem>
                    ))}
                    <StyledMenuItem key={'clear-save'}>
                      <Space size="small">
                        <Button
                          type="text"
                          onClick={clearHandler}
                          size="small"
                          data-cy="clear-button"
                        >
                          Clear
                        </Button>
                        <Button
                          type="primary"
                          onClick={saveHandler}
                          size="small"
                          data-cy="save-button"
                        >
                          Save
                        </Button>
                      </Space>
                    </StyledMenuItem>
                  </StyledMenu>
                )}
                data-cy="showings-calendar-status"
              >
                <Button>
                  <Icon type="antdProfileOutlined" color="black" />
                  Status
                </Button>
              </Dropdown>
            </>
          )}
          {agentUserCalendarContext && <CalendarSyncButton />}
        </>
      ) : (
        <>
          <SearchBar />
          <DateRangePicker value={date} onChange={dateRangeOnChange} />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            open={statusOverlayVisible}
            onOpenChange={visible => setStatusOverlayVisible(visible)}
            dropdownRender={() => (
              <StyledMenu>
                {getStatusMenuItems({
                  isBuyer: selectedMenuOption === AgentCalendarMenu.Buyer,
                }).map(({ label, key }: StatusMenuItem) => (
                  <StyledMenuItem key={key}>
                    <Checkbox
                      onChange={statusOnChange(key)}
                      checked={localStatuses?.includes(key)}
                      id={`${key}-checkbox`}
                      data-cy={`${key}-checkbox`}
                    >
                      {label}
                    </Checkbox>
                  </StyledMenuItem>
                ))}
                <StyledMenuItem key={'clear-save'}>
                  <Space size="small">
                    <Button
                      type="text"
                      onClick={clearHandler}
                      size="small"
                      data-cy="clear-button"
                    >
                      Clear
                    </Button>
                    <Button
                      type="primary"
                      onClick={saveHandler}
                      size="small"
                      data-cy="save-button"
                    >
                      Save
                    </Button>
                  </Space>
                </StyledMenuItem>
              </StyledMenu>
            )}
            data-cy="showings-calendar-status"
          >
            <Button>
              <Icon type="antdProfileOutlined" color="black" />
              Status
            </Button>
          </Dropdown>
          {agentUserCalendarContext && <CalendarSyncButton />}
        </>
      )}

      <EllipsisMenu />
    </StyledHeaderContainer>
  );
};

const StyledHeaderContainer = styled(Space)`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledMenu = styled(Menu)`
  width: 180px;
  && {
    padding: 10px 8px 10px 16px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &&&:hover {
    background-color: ${theme.white};
  }
  &&& > * {
    padding: 5px 7px;
  }
  &:last-child {
    justify-content: flex-end;
  }

  .ant-btn-primary {
    font-weight: 600; //'semi-bold';
  }
`;

const AlignedIcon = styled(Icon)`
  vertical-align: middle;
`;

export default ToolBarDesktop;
